import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function ArticlePage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();

  const article = articles.find((a) => a.id === Number(id));

  useEffect(() => {
    if (article) {
      document.title = `${article.title} | mm131写真图库`;
    } else {
      document.title = '文章未找到 | mm131写真图库';
    }
  }, [article]);

  if (!article) {
    return (
      <div className="pt-16 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200">
            <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
          </div>
          <h2 className="text-xl font-heading font-bold text-foreground-900 mb-2">文章未找到</h2>
          <p className="text-sm text-foreground-500 mb-4">该文章可能已被移除或链接无效</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-home-line"></i>
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const categoryInfo = categories.find((c) => c.id === article.category);
  const relatedArticles = articles
    .filter((a) => article.relatedArticles?.includes(a.id))
    .slice(0, 3);

  const breadcrumbItems = [
    { label: t('breadcrumb.category'), to: '/category' },
    {
      label: categoryInfo?.name || article.category,
      to: `/category/${article.category}`,
    },
    { label: article.title },
  ];

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    image: article.cover,
    publisher: {
      '@type': 'Organization',
      name: 'mm131写真图库',
    },
  };

  return (
    <div className="pt-16">
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      <Breadcrumb items={breadcrumbItems} />

      <article className="w-full py-8 md:py-12 bg-background-50">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="mb-6">
            <Link
              to={`/category/${article.category}`}
              className="inline-flex items-center gap-1 text-xs font-medium text-primary-500 hover:text-primary-600 transition-colors duration-300 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              {categoryInfo?.name || article.category}
            </Link>
          </div>

          <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 leading-tight mb-4">
            {article.title}
          </h1>

          <div className="flex flex-wrap items-center gap-4 text-xs text-foreground-500 mb-6 pb-6 border-b border-background-200/60">
            <span className="flex items-center gap-1">
              <i className="ri-user-line"></i>
              {article.author}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-calendar-line"></i>
              {article.date}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line"></i>
              {article.views.toLocaleString()} {t('home.views')}
            </span>
          </div>

          <div className="flex flex-wrap gap-2 mb-8">
            {article.tags.map((tag) => (
              <span
                key={tag}
                className="px-3 py-1 bg-background-100 border border-background-200/60 rounded-full text-xs text-foreground-600"
              >
                {tag}
              </span>
            ))}
          </div>

          <div className="prose-content space-y-6">
            {article.content.map((block, index) => {
              if (block.type === 'text') {
                return (
                  <p key={index} className="text-sm md:text-base text-foreground-700 leading-relaxed">
                    {block.value}
                  </p>
                );
              }
              if (block.type === 'image') {
                return (
                  <figure key={index} className="my-8">
                    <div className="rounded-xl overflow-hidden">
                      <img
                        src={block.src}
                        alt={block.alt || article.title}
                        className="w-full h-auto"
                        title={block.alt || article.title}
                      />
                    </div>
                    {block.alt && (
                      <figcaption className="mt-2 text-xs text-foreground-400 text-center">
                        {block.alt}
                      </figcaption>
                    )}
                  </figure>
                );
              }
              return null;
            })}
          </div>

          {relatedArticles.length > 0 && (
            <div className="mt-12 pt-8 border-t border-background-200/60">
              <h3 className="text-lg font-heading font-semibold text-foreground-900 mb-5">
                {t('article.relatedArticles')}
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {relatedArticles.map((rel) => (
                  <Link
                    key={rel.id}
                    to={`/article/${rel.id}`}
                    className="group bg-background-100 rounded-xl overflow-hidden hover:scale-[1.02] transition-all duration-300 cursor-pointer"
                  >
                    <div className="aspect-[16/10] overflow-hidden">
                      <img
                        src={rel.cover}
                        alt={rel.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                        title={rel.title}
                      />
                    </div>
                    <div className="p-3">
                      <h4 className="text-xs font-medium text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors duration-300">
                        {rel.title}
                      </h4>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </article>
    </div>
  );
}